import type { Alert, AlertList } from "@cloudserver/contracts";
import type { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BellOff, BellRing } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useMuteAlert } from "../../api/alerts.js";
import { alertQuestion, AskAssistantButton } from "../../assistant/ask.js";
import { ErrorMessage } from "../../ui/feedback.js";
import { dateTime, relativeTime } from "../../ui/format.js";
import { Badge, Button } from "../../ui/primitives.js";
import { DataTable, type Column } from "../../ui/table.js";
import { useToast } from "../../ui/toast.js";
import { alertText, SEVERITY_TONE } from "./text.js";

/** Severity of an alert as a coloured badge. */
export function SeverityBadge({ alert }: { alert: Pick<Alert, "severity"> }) {
  const { t } = useTranslation();
  return (
    <Badge tone={SEVERITY_TONE[alert.severity]}>{t(`alerts.severity.${alert.severity}`)}</Badge>
  );
}

/** Where an alert is: its server (a link to its alerts tab) or its project. */
function Where({ alert, showProject }: { alert: Alert; showProject: boolean }) {
  const { t } = useTranslation();
  return (
    <span className="flex flex-col">
      {alert.serverId && alert.serverName ? (
        <Link
          to="/projects/$projectId/servers/$serverId"
          params={{ projectId: alert.projectId, serverId: alert.serverId }}
          search={{ tab: "alerts" }}
          className="inline-flex min-h-6 items-center whitespace-nowrap text-accent-strong hover:underline"
        >
          {alert.serverName}
        </Link>
      ) : (
        <span className="text-muted">{alert.serverName ?? t("alerts.wholeProject")}</span>
      )}
      {showProject && (
        <Link
          to="/projects/$projectId/alerts"
          params={{ projectId: alert.projectId }}
          className="inline-flex min-h-6 items-center whitespace-nowrap text-xs text-muted hover:underline"
        >
          {alert.projectName}
        </Link>
      )}
    </span>
  );
}

/** Firing, resolved, muted or kept quiet by maintenance. */
export function AlertState({ alert }: { alert: Alert }) {
  const { t, i18n } = useTranslation();
  const time = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { hour: "2-digit", minute: "2-digit" }).format(
      new Date(iso),
    );
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {alert.oneOff ? (
        <Badge>{t("alerts.status.oneOff")}</Badge>
      ) : alert.status === "firing" ? (
        <Badge tone="danger">{t("alerts.status.firing")}</Badge>
      ) : (
        <Badge tone="ok">{t("alerts.status.resolved")}</Badge>
      )}
      {alert.mutedUntil && (
        <Badge>{t("alerts.status.mutedUntil", { time: time(alert.mutedUntil) })}</Badge>
      )}
      {alert.suppressed === "maintenance" && <Badge>{t("alerts.status.maintenance")}</Badge>}
      {alert.suppressed === "muted" && !alert.mutedUntil && (
        <Badge>{t("alerts.status.muted")}</Badge>
      )}
    </span>
  );
}

/** «Заглушить на 1 ч» for a firing alert. */
function MuteButton({ alert }: { alert: Alert }) {
  const { t, i18n } = useTranslation();
  const mute = useMuteAlert(alert.projectId);
  const toast = useToast();
  return (
    <Button
      variant="secondary"
      busy={mute.isPending}
      icon={<BellOff className="size-4" aria-hidden />}
      onClick={() =>
        mute.mutate(
          { alertId: alert.id },
          {
            onSuccess: ({ until }) =>
              toast({
                tone: "ok",
                title: t("alerts.toast.muted", {
                  time: new Intl.DateTimeFormat(i18n.language, {
                    hour: "2-digit",
                    minute: "2-digit",
                  }).format(new Date(until)),
                }),
              }),
            onError: (error) =>
              toast({ tone: "danger", title: t("alerts.toast.muteFailed"), detail: error.message }),
          },
        )
      }
    >
      {t("alerts.actions.mute")}
    </Button>
  );
}

type Pages = UseInfiniteQueryResult<InfiniteData<AlertList>>;

/**
 * Alerts as a table (cards on phones), newest first, with «Показать ещё»
 * for older pages. `canMute` decides per project (owners and administrators).
 */
export function AlertTable({
  label,
  pages,
  showProject = false,
  showWhere = true,
  canMute,
  empty,
}: {
  label: string;
  pages: Pages;
  showProject?: boolean;
  showWhere?: boolean;
  canMute: (alert: Alert) => boolean;
  empty: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  if (pages.error) return <ErrorMessage error={pages.error} />;
  const rows = pages.data?.pages.flatMap((p) => p.items);
  const columns: Column<Alert>[] = [
    {
      key: "what",
      header: t("alerts.columns.what"),
      primary: true,
      // A tablet's table gives the other columns their room first; this one keeps enough
      // for two words a line.
      className: "min-w-44",
      cell: (a) => (
        // In a narrow column (a tablet) the text goes under its badge rather than one word
        // per line beside it.
        <span className="flex flex-wrap items-start gap-x-2 gap-y-1">
          <SeverityBadge alert={a} />
          <span className="min-w-0 grow basis-40 break-words">
            {alertText(t, a, i18n.language)}
          </span>
        </span>
      ),
    },
    ...(showWhere
      ? [
          {
            key: "where",
            header: t("alerts.columns.where"),
            cell: (a: Alert) => <Where alert={a} showProject={showProject} />,
          },
        ]
      : []),
    {
      key: "started",
      header: t("alerts.columns.started"),
      sort: (a) => a.startedAt,
      cell: (a) => (
        <time dateTime={a.startedAt} title={dateTime(a.startedAt, i18n.language)}>
          {relativeTime(a.startedAt, i18n.language)}
        </time>
      ),
    },
    {
      key: "state",
      header: t("alerts.columns.state"),
      cell: (a) => <AlertState alert={a} />,
    },
    {
      key: "actions",
      header: <span className="sr-only">{t("table.actions")}</span>,
      align: "right",
      cell: (a) => (
        <span className="inline-flex flex-wrap items-center justify-end gap-1">
          <AskAssistantButton
            compact
            request={alertQuestion(t, a, alertText(t, a, i18n.language))}
          />
          {a.status === "firing" && !a.oneOff && !a.mutedUntil && canMute(a) && (
            <MuteButton alert={a} />
          )}
        </span>
      ),
    },
  ];
  return (
    <div className="flex flex-col gap-3">
      <DataTable
        label={label}
        rows={rows}
        rowKey={(a) => a.id}
        columns={columns}
        loading={pages.isPending}
        empty={empty}
      />
      {pages.hasNextPage && (
        <div>
          <Button
            variant="secondary"
            busy={pages.isFetchingNextPage}
            onClick={() => void pages.fetchNextPage()}
          >
            {t("alerts.actions.loadMore")}
          </Button>
        </div>
      )}
    </div>
  );
}

/** The usual empty state of a list of alerts. */
export function QuietEmpty({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-line bg-surface px-6 py-10 text-center">
      <BellRing className="size-6 text-ok" aria-hidden />
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="max-w-md text-sm text-muted">{children}</p>
    </div>
  );
}
